import React, { forwardRef } from 'react';
import { IMaskInput } from 'react-imask';
import * as Yup from 'yup';
import { ROUTES } from 'routes/constants';
import { cpfValidation } from 'utils/yup_cpf_validation';

export interface ICreateCuffaFamilyDataDto {
  family_unit_name: string;
  community_name: string;
  reference_year: number;
  informant_full_name: string;
  informant_cpf: string;
  informant_birth_date: string;
  informant_gender: string;
  informant_phone: string;
  informant_marital_status: string;
  spouse_full_name?: string;
  spouse_birth_date?: string;
  spouse_gender?: string;
  family_members_count: number;
  institution_uuid?: string;
}

export enum CuffaGenderEnum {
  MASCULINO = 'MASCULINO',
  FEMININO = 'FEMININO',
  PREFIRO_NAO_RESPONDER = 'PREFIRO NÃO RESPONDER',
}

export enum CuffaMaritalStatusEnum {
  SOLTEIRO = 'SOLTEIRO',
  CASADO = 'CASADO',
  VIUVO = 'VIUVO',
  UNIAO_ESTAVEL = 'UNIÃO ESTÁVEL',
  SEPARADO_DIVORCIADO = 'SEPARADO/DIVORCIADO',
}

export const getMaritalStatusEnum = (status: string): CuffaMaritalStatusEnum => {
  switch (status?.toUpperCase?.()) {
    case 'CASADA(O)': return CuffaMaritalStatusEnum.CASADO;
    case 'UNIÃO ESTÁVEL': return CuffaMaritalStatusEnum.UNIAO_ESTAVEL;
    case 'VIÚVA(O)': return CuffaMaritalStatusEnum.VIUVO;
    case 'SEPARADA(O)/DIVORCIADA(O)': return CuffaMaritalStatusEnum.SEPARADO_DIVORCIADO;
    default: return CuffaMaritalStatusEnum.SOLTEIRO;
  }
};

export const getGenderEnum = (gender: string): CuffaGenderEnum => {
  switch (gender?.toUpperCase?.()) {
    case 'MASCULINO': return CuffaGenderEnum.MASCULINO;
    case 'FEMININO': return CuffaGenderEnum.FEMININO;
    default: return CuffaGenderEnum.PREFIRO_NAO_RESPONDER;
  }
};

export const getMaritalStatusFormValue = (statusEnum?: CuffaMaritalStatusEnum): string => {
  if (!statusEnum) return '';
  switch (statusEnum) {
    case CuffaMaritalStatusEnum.SOLTEIRO: return 'SOLTEIRA(O)';
    case CuffaMaritalStatusEnum.CASADO: return 'CASADA(O)';
    case CuffaMaritalStatusEnum.UNIAO_ESTAVEL: return 'UNIÃO ESTÁVEL';
    case CuffaMaritalStatusEnum.VIUVO: return 'VIÚVA(O)';
    case CuffaMaritalStatusEnum.SEPARADO_DIVORCIADO: return 'SEPARADA(O)/DIVORCIADA(O)';
    default: return '';
  }
};

export const getGenderFormValue = (genderEnum?: CuffaGenderEnum): string => {
  if (!genderEnum) return '';
  switch (genderEnum) {
    case CuffaGenderEnum.FEMININO: return 'Feminino';
    case CuffaGenderEnum.MASCULINO: return 'Masculino';
    case CuffaGenderEnum.PREFIRO_NAO_RESPONDER: return 'Prefiro não responder';
    default: return '';
  }
};

interface MaskProps {
  name: string;
  onChange: (event: { target: { name: string; value: string } }) => void;
}

export const TextMaskTelefone = forwardRef<HTMLInputElement, MaskProps>(function TextMaskTelefone(props, ref) {
  const { onChange, name, ...other } = props;
  // @ts-ignore
  return (
    <IMaskInput
      {...other}
      mask="(00) 00000-0000"
      definitions={{ '#': /[1-9]/ }}
      inputRef={ref}
      overwrite
      onAccept={(value: any) => onChange({ target: { name, value: String(value) } })}
    />
  );
});

export const TextMaskCpf = forwardRef<HTMLInputElement, MaskProps>(function TextMaskCpf(props, ref) {
  const { onChange, name, ...other } = props;
  // @ts-ignore
  return (
    <IMaskInput
      {...other}
      mask="000.000.000-00"
      inputRef={ref}
      overwrite
      onAccept={(value: any) => onChange({ target: { name, value: String(value) } })}
    />
  );
});

export const estadoCivilComConjuge = ['CASADA(O)', 'UNIÃO ESTÁVEL'];

export const isValidCpf = (cpfRaw?: string | null): boolean => {
  if (!cpfRaw) return false;
  const cpf = String(cpfRaw).replace(/\D/g, '');
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  const computeCheckDigit = (base: string, factor: number) => {
    let total = 0;
    for (let i = 0; i < base.length; i++) {
      total += parseInt(base.charAt(i), 10) * (factor - i);
    }
    const remainder = total % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const nine = cpf.substring(0, 9);
  const digit1 = computeCheckDigit(nine, 10);
  const ten = nine + String(digit1);
  const digit2 = computeCheckDigit(ten, 11);

  return digit1 === parseInt(cpf.charAt(9), 10) && digit2 === parseInt(cpf.charAt(10), 10);
};

export const cleanCpf = (cpfRaw?: string) => String(cpfRaw || '').replace(/\D/g, '');

const validateBirthDate = (value: string | undefined): boolean => {
  if (!value) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(value);
  if (isNaN(date.getTime())) return false;
  const year = date.getFullYear();
  const currentYear = new Date().getFullYear();
  if (year < 1900 || year > currentYear) return false;
  if (date > new Date()) return false;
  return true;
};

export const validationSchema = Yup.object().shape({
  nomeUnidadeFamiliar: Yup.string().max(100),
  nomeComunidade: Yup.string().max(100).required('Campo obrigatório'),
  nomeInformante: Yup.string().required('Campo obrigatório'),
  genero: Yup.string().required('Campo obrigatório'),
  estadoCivil: Yup.string().required('Campo obrigatório'),
  dataNascimento: Yup.string()
    .required('Campo obrigatório')
    .test('valid-year', 'O ano deve estar entre 1900 e o ano atual', validateBirthDate)
    .test('not-future', 'A data não pode ser futura', (value) => {
      if (!value) return false;
      const date = new Date(value);
      return date <= new Date();
    }),
  telefone: Yup.string().required('Campo obrigatório'),
  qtdPessoas: Yup.number()
    .integer('Deve ser um número inteiro')
    .typeError("Deve ser um número")
    .min(1, 'A quantidade deve ser no mínimo 1')
    .max(50, 'A quantidade deve ser no máximo 50')
    .required('Campo obrigatório'),
  nomeConjuge: Yup.string().when('estadoCivil', {
    is: (val: string) => estadoCivilComConjuge.includes(val),
    then: (schema) => schema.required('Campo obrigatório'),
  }),
  dataNascimentoConjuge: Yup.string().when('estadoCivil', {
    is: (val: string) => estadoCivilComConjuge.includes(val),
    then: (schema) => schema
      .required('Campo obrigatório')
      .test('valid-year', 'O ano deve estar entre 1900 e o ano atual', validateBirthDate)
      .test('not-future', 'A data não pode ser futura', (value) => {
        if (!value) return false;
        const date = new Date(value);
        return date <= new Date();
      }),
  }),
  generoConjuge: Yup.string().when('estadoCivil', {
    is: (val: string) => estadoCivilComConjuge.includes(val),
    then: (schema) => schema.required('Campo obrigatório'),
  }),
  cpf: cpfValidation('Campo obrigatório'),

});

export default {} as any;

// Shared payload / mapping helpers
export const buildPayload = (values: any) => {
  const payload: any = {
    family_unit_name: values.nomeUnidadeFamiliar || null,
    community_name: values.nomeComunidade,
    reference_year: new Date().getFullYear(),
    informant_full_name: values.nomeInformante,
    informant_cpf: values.cpf,
    informant_birth_date: values.dataNascimento,
    informant_gender: getGenderEnum(values.genero),
    informant_phone: values.telefone,
    informant_marital_status: getMaritalStatusEnum(values.estadoCivil),
    family_members_count: parseInt(values.qtdPessoas || '0', 10) || 0,
  };

  if (estadoCivilComConjuge.includes(values.estadoCivil)) {
    payload.spouse_full_name = values.nomeConjuge;
    payload.spouse_gender = getGenderEnum(values.generoConjuge);
    payload.spouse_birth_date = values.dataNascimentoConjuge;
  }

  return payload;
};

export const mapApiToFormValues = (apiData: any) => ({
  nomeUnidadeFamiliar: apiData.family_unit_name || '',
  nomeComunidade: apiData.community_name || '',
  nomeInformante: apiData.informant_full_name || '',
  cpf: apiData.informant_cpf || '',
  dataNascimento: apiData.informant_birth_date || '',
  genero: getGenderFormValue(apiData.informant_gender),
  telefone: apiData.informant_phone || '',
  estadoCivil: getMaritalStatusFormValue(apiData.informant_marital_status),
  nomeConjuge: apiData.spouse_full_name || '',
  dataNascimentoConjuge: apiData.spouse_birth_date || '',
  generoConjuge: getGenderFormValue(apiData.spouse_gender),
  qtdPessoas: String(apiData.family_members_count || ''),
});

export const mapApiToPartial = (updatedData: any) => ({
  identificacaoFamiliar: {
    nomeUnidadeFamiliar: updatedData.family_unit_name || '',
    nomeComunidade: updatedData.community_name || '',
    cpf: updatedData.informant_cpf || '',
    nomeInformante: updatedData.informant_full_name || '',
    dataNascimento: updatedData.informant_birth_date || '',
    genero: getGenderFormValue(updatedData.informant_gender),
    dataNascimentoConjuge: updatedData.spouse_birth_date || '',
    qtdPessoas: String(updatedData.family_members_count || ''),
    telefone: updatedData.informant_phone || '',
    estadoCivil: getMaritalStatusFormValue(updatedData.informant_marital_status),
    nomeConjuge: updatedData.spouse_full_name || '',
    generoConjuge: getGenderFormValue(updatedData.spouse_gender),
  },
});

export const setPartialFromApiResponse = (response: any, store: any, fallbackValues: any) => {
  const updatedData = response?.dados;
  if (updatedData) {
    store.salvarDadosParciais({ identificacaoFamiliar: mapApiToFormValues(updatedData) });
    return true;
  }
  store.salvarDadosParciais({ identificacaoFamiliar: fallbackValues });
  return false;
};

export const getPostSaveRoute = (collectionUuid: string, advanceNextFlag?: boolean) => {
  if (advanceNextFlag) return ROUTES.VISITAS_INSTITUICOES_CUFFA_INCLUIR.replace(':uuid', collectionUuid);
  return ROUTES.CUFFA;
};

export const submitFamilyData = async (
  params: {
    values: any;
    uuid?: string;
    cuffaApi: any;
    store: any;
    advanceNext?: boolean;
  },
) => {
  const { values, uuid, cuffaApi, store } = params;
  const payload = buildPayload(values);

  let collectionUuid: string | undefined;

  if (uuid) {
    await cuffaApi.updateFamilyData(uuid, payload);
    try {
      const updatedResponse = await cuffaApi.getDataByID(uuid);
      setPartialFromApiResponse(updatedResponse, store, values);
    } catch (err) {
      store.salvarDadosParciais({ identificacaoFamiliar: values });
    }
    collectionUuid = uuid;
    return { success: true, collectionUuid, message: 'Dados da família atualizados com sucesso!' };
  }

  // create flow
  const cleanCpfVal = cleanCpf(values.cpf);
  let existingUuid: string | undefined;
  try {
    const checkResponse = await cuffaApi.checkExistingCpf(cleanCpfVal);
    existingUuid = checkResponse.dados?.uuid;
  } catch (err) {
    existingUuid = undefined;
  }

  if (existingUuid) {
    await cuffaApi.updateFamilyData(existingUuid, payload);
    try {
      const updatedResponse = await cuffaApi.getDataByID(existingUuid);
      setPartialFromApiResponse(updatedResponse, store, values);
    } catch (err) {
      store.salvarDadosParciais({ identificacaoFamiliar: values });
    }
    collectionUuid = existingUuid;
    return { success: true, collectionUuid, message: 'Dados da família atualizados com sucesso!' };
  }

  const createResponse = await cuffaApi.createManualCollection(payload);
  const newUuid = createResponse.dados?.uuid;
  if (newUuid) {
    collectionUuid = newUuid;
    store.salvarDadosParciais({ identificacaoFamiliar: values });
    return { success: true, collectionUuid, message: 'Família cadastrada com sucesso!' };
  }

  throw new Error('UUID da coleção não foi retornado pela API após a criação.');
};
